import * as React from 'react';
import { every } from 'lodash';
import Flex from 'ui/Flex/Flex';
import { Autocomplete } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useDebounce } from 'hooks/helpers';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useSelector } from 'store/hooks';
import { capitalize } from 'helpers/helpers';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { useAlertForm } from '../Contexts/AlertFormContext';
import PreviousStepButton from './PreviousStepButton';
import SubmitButton from './SubmitButton';
import { isAlertFilterValid, isThresholdValid } from './validators';
import { useAlertAction } from '../Contexts/AlertActionContext';
import { useAlertSentence } from '../Contexts/AlertSentenceContext';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import { useGetPossibleRecipients } from 'lib/alert/hooks/useFetchRecipients';

const StyEmailTextField = styled(TextField, {
    name: 'StyEmailTextField',
})({
    marginLeft: '0.5rem',
    minWidth: '17rem',
});

type Props = {
    chartKey: number;
};

const AlertSharings = ({ chartKey }: Props): JSX.Element => {
    const { t } = useTranslation();

    const { alert, setAlert } = useAlertForm();
    const { availableMetrics, availableFilters } = useAlertSentence();
    const { action } = useAlertAction();
    const user = useSelector((state) => state.vision.user);
    const dashboard = useGetDashboardFromSlugOrMetadata();
    const [alertRecipients, setAlertRecipients] = useState<Array<string>>([]);

    const { data: possibleRecipientEmails = [] } = useGetPossibleRecipients(
        dashboard?.key,
        (users) => users.map((u) => u.email),
    );

    const updateRecipient = useDebounce((recipients: string[]) => {
        setAlert((prevState) => ({
            ...prevState,
            recipientsEmails: recipients,
        }));
    }, 500);

    const ensureThatUserEmailIsPresentAndAtFirstPosition = (
        formValues: string[],
    ) => [user.email, ...formValues.filter((value) => value !== user.email)];

    const onRecipientsChange = (values: string[]) => {
        const valuesKeepingUserEmailFirst =
            ensureThatUserEmailIsPresentAndAtFirstPosition(values);
        setAlertRecipients(valuesKeepingUserEmailFirst);
        updateRecipient(valuesKeepingUserEmailFirst);
    };

    useEffect(() => {
        // We initialize the recipients with the following rules.
        // Use the recipients of the alerts if there are any
        // Otherwise use the logged in user
        const defaultRecipients = alert.recipientsEmails ?? [user.email];
        onRecipientsChange(defaultRecipients);
    }, []);

    const isDisabled = (): boolean => {
        if (action.name === 'create') {
            return !(
                alert.recipientsEmails && alert.recipientsEmails.length > 0
            );
        }
        return !(
            alert.type &&
            alert.metric &&
            alert.condition?.operation &&
            isThresholdValid(alert.condition?.threshold.toString()) &&
            alert.name?.trim() &&
            alert.emailSubject &&
            availableMetrics &&
            availableFilters &&
            every(alert.filters, isAlertFilterValid)
        );
    };

    return (
        <Flex direction="column" sx={{ mt: 2 }}>
            <Autocomplete
                multiple
                limitTags={2}
                id="tags-filled"
                options={possibleRecipientEmails}
                getOptionLabel={(option) => option}
                filterSelectedOptions
                value={alertRecipients}
                noOptionsText={t('sendDashboard.noOptions')}
                onChange={(event, value) => onRecipientsChange(value)}
                autoSelect
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...otherProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                variant="outlined"
                                label={option}
                                {...otherProps}
                                disabled={option === user.email}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <Tooltip
                        title={capitalize(t('sendDashboard.recipientTips'))}
                    >
                        <StyEmailTextField
                            {...params}
                            variant="outlined"
                            label={capitalize(t('sendDashboard.recipients'))}
                            name="email"
                            type="email"
                        />
                    </Tooltip>
                )}
            />
            <Flex
                align="center"
                sx={{ gap: 2, marginTop: '2em', marginBottom: '2em' }}
            >
                {action.name === 'create' && <PreviousStepButton />}
                <SubmitButton
                    disabled={isDisabled()}
                    action={action}
                    chartKey={chartKey}
                />
            </Flex>
        </Flex>
    );
};

export default memo(AlertSharings);
